// src/components/IPAddressList.jsx
import {
  Block as BlockIcon,
  Flag as FlagIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  Avatar,
  CircularProgress
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ExportCsvButton from "../../components/ExportCsvButton";
import RowPerPage from "../../components/RowPerPage";
import DropdownFilter from "../../components/ui/DropdownFilter";
import { DatePicker } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Pagination from "../../components/Pagination";
import IPDetailsModal from "./IPDetailsModal";
import { useIpList, useIpDetails } from "../../features/location/useIpLookup";
import { useLocationOverview } from "../../features/location/useLocation";
// Columns definition for Export CSV
const ipExportColumns = [
  { label: "IP Address", key: "ipAddress" },
  { label: "Location", key: "location" },
  { label: "User", key: "user" },
  { label: "Last Access", key: "lastAccess" },
  { label: "Logins", key: "logins" },
  { label: "Registrations", key: "registrations" },
  { label: "Total Users", key: "totalUsers" },
  { label: "Status", key: "status" },
];


// Flag image CDN links helper
const getFlagUrl = (code) => {
  if (!code) return "";
  return `https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/${code.toLowerCase()}.svg`;
};

export default function IPAddressList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    text: { primary: textPrimary, secondary: textSecondary },
    divider,
    primary: { main: primaryMain, dark: primaryDark },
    error: { main: errorMain },
    warning: { main: warningMain },
    action: { disabledBackground },
  } = theme.palette;

  // states & handlers
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [detailsIp, setDetailsIp] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [appliedFromDate, setAppliedFromDate] = useState(null);
  const [appliedToDate, setAppliedToDate] = useState(null);

  // Auto-handle search from URL
  useEffect(() => {
    const searchParam = searchParams.get('search');
    if (searchParam) {
      setSearch(searchParam);
      // If it looks like an IP, automatically open details
      if (/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(searchParam)) {
        setDetailsIp(searchParam);
        setDetailsOpen(true);
      }
    }
  }, [searchParams]);

  const { data: locationOverview } = useLocationOverview();

  // Data Fetching
  const { data, isLoading } = useIpList({
    page,
    limit: rowsPerPage,
    search,
    country: selectedCountry,
    flaggedOnly,
    fromDate: appliedFromDate,
    toDate: appliedToDate
  });

  const { items: displayedIpAddressList = [], total = 0 } = data || {};

  // For details modal
  const { data: detailData } = useIpDetails(detailsIp, detailsOpen);

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedIp, setSelectedIp] = useState(null);

  const handleMenuOpen = (event, ip) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedIp(ip);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleChangePage = (newPage) => setPage(newPage);
  const handleOpenDetails = (ip) => {
    setDetailsIp(ip);
    setDetailsOpen(true);
  };
  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setDetailsIp(null);
  };

  const handleAction = (type) => {
    if (type === "details") handleOpenDetails(selectedIp);
    else console.log(`${type} IP:`, selectedIp);
    handleMenuClose();
  };

  const handleDateSearch = () => {
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setPage(0); // Reset to first page when applying filters
  };

  // Status colors
  const getStatusColor = (status) => {
    if (theme.palette.mode === "dark") {
      switch (status) {
        case "Safe":
          return { bg: "#1e4620", text: "#4caf50" };
        case "Flagged":
          return { bg: "#553600", text: "#ff9800" };
        case "Pending":
          return { bg: "#553600", text: "#ff9800" };
        case "Blocked":
          return { bg: "#591212", text: "#f44336" };
        case "Failed":
          return { bg: "#591212", text: "#f44336" };
        default:
          return { bg: "#1e4620", text: "#4caf50" };
      }
    } else {
      // Light mode
      switch (status) {
        case "Safe":
          return { bg: "transparent", text: "#000000" };
        case "Flagged":
          return { bg: "transparent", text: "#ff9800" };
        case "Pending":
          return { bg: "transparent", text: "#ff9800" };
        case "Blocked":
          return { bg: "transparent", text: "#ff0000" };
        case "Failed":
          return { bg: "transparent", text: "#ff0000" };
        default:
          return { bg: "transparent", text: "#4caf50" };
      }
    }
  };

  const outlineSX = {
    "& fieldset": { borderColor: divider },
    "&:hover fieldset": { borderColor: warningMain },
    "&.Mui-focused fieldset": { borderColor: primaryMain },
  };

  return (
    <Box
      sx={{
        width: "100%",
        color: textPrimary,
        p: 3,
        boxShadow: "none",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        fontSize: "1.1rem",
        "& .MuiTypography-root": { fontSize: "1.0.5em" },
        "& .MuiTableCell-root": { fontSize: "1.em" },
        "& .MuiChip-label": { fontSize: "1.05em" },
        "& .MuiButton-root": { fontSize: "1.05em" },
        "& .MuiMenuItem-root": { fontSize: "1.05em" },
        "& .MuiInputBase-input": { fontSize: "1.05em" },
      }}
    >
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Date filters + Search button (Visual only for now as API for date filter not requested specifically but I kept UI) */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: "flex",
              gap: 4,
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{ mb: 2, color: textPrimary }}
            >
              {" "}
              IP Address List
            </Typography>

            <RowPerPage
              rowsPerPage={rowsPerPage}
              onRowsChange={(n) => {
                setRowsPerPage(n);
                setPage(0);
              }}
              options={[10, 20, 30]}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignContent: "center",
                justifyContent: "center",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    label="From"
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          height: "40px",
                          "& .MuiInputBase-root": { height: "100%" },
                        },
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    label="To"
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          mb: 0.5,
                          height: "41px",
                          "& .MuiInputBase-root": { height: "100%" },
                        },
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>

              <Button
                variant="contained"
                onClick={handleDateSearch}
                sx={{
                  mt: 1,
                  bgcolor: primaryMain,
                  "&:hover": { bgcolor: primaryDark },
                  height: "40px",
                  width: "130px",
                }}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Search + Export + Filters */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              justifyContent: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <TextField
              placeholder="Search by IP, location, username, or email"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
            {/* Replaced Dropdown with simple text input for Country filter as we don't have list */}
            <DropdownFilter
              title="Country"
              options={["All", ...(locationOverview?.countryData?.map(c => c.name) || [])]}
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
            />

            {/* <FormControlLabel
              control={
                <Switch
                  checked={flaggedOnly}
                  onChange={(e) => setFlaggedOnly(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: primaryMain,
                      "&:hover": {
                        backgroundColor: alpha(disabledBackground, 0.1),
                      },
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: primaryMain,
                    },
                  }}
                />
              }
              label="Flagged Only"
              sx={{ color: textPrimary }}
            /> */}
          </Box>
        </Grid>
      </Grid>

      {/* Table */}
      <TableContainer
        sx={{
          boxShadow: "none",
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(2),
          borderRadius: 2,
        }}
      >
        <Table>
          <TableHead
            sx={{ color: textPrimary, borderBottom: `1px solid ${divider}` }}
          >
            <TableRow>
              {[
                "IP Address",
                "Location",
                "User Associated",
                "Last Access Time",
                "Activity (Logins/Registrations)",
                "Status",
                "Action",
              ].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    color: textPrimary,
                    borderBottom: `1px solid ${divider}`,
                    fontSize: "1.2rem",
                    fontWeight: "500px",
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center"><CircularProgress /></TableCell>
              </TableRow>
            ) : (
              displayedIpAddressList.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ "&:hover": { bgcolor: alpha(disabledBackground, 0.1) } }}
                >
                  <TableCell
                    sx={{
                      color: textPrimary,
                      borderBottom: `1px solid ${divider}`,
                    }}
                  >
                    {row.ipAddress}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: textPrimary,
                      borderBottom: `1px solid ${divider}`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <img
                        src={getFlagUrl(row.flag)}
                        alt={row.flag}
                        style={{ width: "20px", height: "15px" }}
                      />
                      {row.location}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: textPrimary,
                      borderBottom: `1px solid ${divider}`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* User profile image not in IP list API yet, using generic or name initial */}
                      <Avatar
                        sx={{ width: 24, height: 24, fontSize: '0.8rem' }}
                      >
                        {row.user.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.user}
                      </Typography>
                      {row.lastUserId && (
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/users?userId=${row.lastUserId}`)}
                          sx={{ color: primaryMain }}
                          title="View User Details"
                        >
                          <ArrowForwardIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: textPrimary,
                      borderBottom: `1px solid ${divider}`,
                    }}
                  >
                    <Typography>{row.lastAccess}</Typography>
                    <Typography variant="body2" sx={{ color: textSecondary }}>
                      {row.lastAccessTime}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: textPrimary,
                      borderBottom: `1px solid ${divider}`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2">
                        {row.logins || 0} logins
                      </Typography>
                      {row.registrations > 0 && (
                        <>
                          <Typography variant="body2" sx={{ color: textSecondary }}>
                            •
                          </Typography>
                          <Typography variant="body2" color="primary">
                            {row.registrations} registrations
                          </Typography>
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${divider}` }}>
                    <Chip
                      label={row.status}
                      size="small"
                      sx={{
                        bgcolor: getStatusColor(row.status).bg,
                        color: getStatusColor(row.status).text,
                        fontWeight: "medium",
                        borderRadius: 1,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${divider}` }}>
                    <IconButton onClick={(e) => handleMenuOpen(e, row.ipAddress)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => handleAction("details")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: primaryMain,
          }}
        >
          <VisibilityIcon fontSize="small" />
          View Details
        </MenuItem>
        {/* <MenuItem
          onClick={() => handleAction("block")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: errorMain,
          }}
        >
          <BlockIcon fontSize="small" />
          Block IP
        </MenuItem>
        <MenuItem
          onClick={() => handleAction("flag")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: warningMain,
          }}
        >
          <FlagIcon fontSize="small" />
          Flag IP
        </MenuItem> */}
      </Menu>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, total)} of {total} IP addresses
        </Typography>
        <Pagination
          page={page}
          count={total}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
        />
      </Box>

      {/* Details Modal */}
      {/* We need to transform backend detailedIp to expected format or update Modal? 
          Assuming Modal expects detailViewData array or single item.
          Existing Code used detailViewData array and just opened it.
          I'll pass the fetched detail data as single item list or update the Modal usage.
      */}
      <IPDetailsModal
        open={detailsOpen}
        onClose={handleCloseDetails}
        detailViewData={detailData?.sessions || []}
        ipData={detailData || {}}
      />
    </Box>
  );
}
